<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUnitsApi } from '@/composables/useUnitsApi';
import { useRentalFilters } from '@/composables/useRentalFilters';
import { usePurchaseFilters } from '@/composables/usePurchaseFilters';
import { usePurchaseLocalizedUrls } from '@/composables/usePurchaseLocalizedUrls';
import { useRentalLocalizedUrls } from '@/composables/useRentalLocalizedUrls';
import { useRegionsApi } from '@/composables/useRegionsApi';
import { DEFAULT_MIN_PRICE, DEFAULT_MAX_PRICE, DEFAULT_RENTAL_UNIT_STATUSES, DEFAULT_RENTAL_VALID_UNIT_STATUSES } from '@/constants/rentalConstants';
import UnitsPriceRangeFilter from '@/components/units/filters/UnitsPriceRangeFilter.vue';
import UnitsFloorSelectorFilter from '@/components/units/filters/UnitsFloorSelectorFilter.vue';
import RegionSearchField from './RegionSearchField.vue';
import SizeSearchField from './SizeSearchField.vue';
import AnimalSearchField from './AnimalSearchField.vue';
import DateSearchField from './DateSearchField.vue';
import type { NumberRange } from '@/typings/NumberRange';
import { DEFAULT_PURCHASE_MIN_PRICE, DEFAULT_PURCHASE_MAX_PRICE, DEFAULT_PURCHASE_UNIT_STATUSES, DEFAULT_PURCHASE_VALID_UNIT_STATUSES } from '@/constants/purchaseConstants';
import { ListingTypeEnum } from '@/enums/ListingType';

interface FilterOptions {
  selectedFloorNo: number | undefined
  selectedPriceRange: NumberRange
  selectedRegion: number | null
  selectedSize: string[] | null
  selectedDate: string[] | null
}

interface RentalFilterOptions extends FilterOptions {  
  dogAllowed: boolean | null
}

const props = defineProps<{
  show: boolean
  applyButtonText?: string
  unitsSearch?: boolean
  listingType?: ListingTypeEnum
}>();

const emit = defineEmits<{
  close: []
  apply: [filters: FilterOptions | RentalFilterOptions]
  clearFilters: []
}>();

const { t } = useI18n();
const { getUnitFilters } = useUnitsApi();
const { loadFiltersFromRentalStorage, saveFiltersToRentalStorage } = useRentalFilters();
const { loadFiltersFromPurchaseStorage, saveFiltersToPurchaseStorage } = usePurchaseFilters();
const { getRegionWizardUrl } = useRentalLocalizedUrls();

// Exposer l'enum pour le template
const ListingType = ListingTypeEnum;

// Computed property pour accéder à dogAllowed de manière sûre
const rentalFilters = computed(() => currentFilters.value as RentalFilterOptions);
const { getAllRegions } = useRegionsApi();

const currentFilters = ref<FilterOptions | RentalFilterOptions>({
  selectedFloorNo: undefined,
  selectedPriceRange: {},
  selectedRegion: null,
  selectedSize: null,
  selectedDate: null,
  dogAllowed: null
});

const availableFloors = ref<number[]>([]);
const priceRange = ref({
  min: props.listingType === ListingTypeEnum.Rental ? DEFAULT_MIN_PRICE : DEFAULT_PURCHASE_MIN_PRICE,
  max: props.listingType === ListingTypeEnum.Rental ? DEFAULT_MAX_PRICE : DEFAULT_PURCHASE_MAX_PRICE
});
const originalRegionId = ref<number | null>(null);
const isFirstLoad = ref(true); // Pour s'assurer qu'on garde la vraie région originale
const sessionOriginalRegion = ref<number | null>(null); // Région au début de la session de navigation

const floorOptions = computed(() => {
  return availableFloors.value;
});

const closePopup = () => {
  emit('close');
};

const loadExistingFilters = () => {
  const savedFilters = props.listingType === ListingTypeEnum.Rental ? loadFiltersFromRentalStorage() : loadFiltersFromPurchaseStorage();
  const params = new URLSearchParams(window.location.search);
  
  if (!props.unitsSearch) {
    // Stocker la région originale SEULEMENT au premier chargement
    if (isFirstLoad.value) {
      originalRegionId.value = savedFilters.regionId;
      isFirstLoad.value = false;
    }
    
    currentFilters.value = {
      selectedFloorNo: savedFilters.floors && savedFilters.floors.length > 0 ? savedFilters.floors[0] : undefined,
      selectedPriceRange: {
        min: savedFilters.minPrice,
        max: savedFilters.maxPrice
      },
      selectedRegion: savedFilters.regionId,
      selectedSize: savedFilters.unitConfigurations || null,
      selectedDate: Array.isArray(savedFilters.availabilityDates) ? savedFilters.availabilityDates : null
    };

    if (props.listingType === ListingTypeEnum.Rental) {
      (currentFilters.value as RentalFilterOptions).dogAllowed = (savedFilters as unknown as RentalFilterOptions).dogAllowed !== undefined ? (savedFilters as unknown as RentalFilterOptions).dogAllowed : null;
    }
  } else {
    currentFilters.value = {
      selectedFloorNo: params.get('floors') ? Number(params.get('floors')) : undefined,
      selectedPriceRange: {
        min: params.get('minPrice') ? Number(params.get('minPrice')) : undefined,
        max: params.get('maxPrice') ? Number(params.get('maxPrice')) : undefined
      },
      selectedRegion: params.get('regionId') ? Number(params.get('regionId')) : null,
      selectedSize: params.get('unitConfigurations') ? params.get('unitConfigurations')!.split(';') : null,
      selectedDate: params.get('availabilityDates') ? params.get('availabilityDates')!.split(';') : null
    };

    if (props.listingType === ListingTypeEnum.Rental) {
      (currentFilters.value as RentalFilterOptions).dogAllowed = params.get('dogAllowed') ? params.get('dogAllowed') === '1' : null;
    }
  }
};

// Watcher pour détecter l'ouverture/fermeture du popup
watch(() => props.show, (isShown) => {
  if (isShown) {
    // Popup ouvert : charger les filtres et définir la région originale
    loadExistingFilters();
  } else {
    // Popup fermé : réinitialiser pour la prochaine ouverture
    isFirstLoad.value = true;
  }
});

const clearAllFilters = () => {

  const params = new URLSearchParams(window.location.search);
  params.delete('floors');
  params.delete('minPrice');
  params.delete('maxPrice');
  params.delete('regionId');
  params.delete('unitConfigurations');
  params.delete('availabilityDates');
  params.delete('dogAllowed');
  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({}, '', newUrl);

  currentFilters.value = {
    selectedFloorNo: undefined,
    selectedPriceRange: {},
    selectedRegion: !props.unitsSearch ? currentFilters.value.selectedRegion : null,
    selectedSize: null,
    selectedDate: null,
    dogAllowed: null
  };
  
  // Effacer tous les filtres du localStorage
  if (props.listingType === ListingTypeEnum.Rental) {
    saveFiltersToRentalStorage({
      regionId: undefined,
      unitConfigurations: undefined,
      availabilityDates: undefined,
      dogAllowed: undefined,
      floors: undefined,
      minPrice: undefined,
      maxPrice: undefined
    });
  } else {
    saveFiltersToPurchaseStorage({
      regionId: !props.unitsSearch ? currentFilters.value.selectedRegion : undefined,
      unitConfigurations: undefined,
      availabilityDates: undefined,
      floors: undefined,
      minPrice: undefined,
      maxPrice: undefined
    });
  }
  
  // Émettre l'événement pour notifier InlineSearchBar
  emit('clearFilters');
};

const applyFilters = async () => {
  // Vérifier si la région a changé depuis le début de la session
  const regionHasChanged = sessionOriginalRegion.value !== currentFilters.value.selectedRegion;
  
  // Sauvegarder les filtres dans le localStorage
  const filtersToSave = {
    regionId: currentFilters.value.selectedRegion,
    unitConfigurations: currentFilters.value.selectedSize || undefined,
    availabilityDates: currentFilters.value.selectedDate || undefined,
    floors: currentFilters.value.selectedFloorNo !== undefined ? [currentFilters.value.selectedFloorNo] : undefined,
    minPrice: currentFilters.value.selectedPriceRange?.min,
    maxPrice: currentFilters.value.selectedPriceRange?.max
  };
  
  if (props.listingType === ListingTypeEnum.Rental) {
    (filtersToSave as unknown as RentalFilterOptions).dogAllowed = (currentFilters.value as RentalFilterOptions).dogAllowed !== null ? (currentFilters.value as RentalFilterOptions).dogAllowed : null;
    saveFiltersToRentalStorage(filtersToSave);
  } else {
    saveFiltersToPurchaseStorage(filtersToSave);
  }
  
  if (props.unitsSearch) {
    const params = new URLSearchParams(window.location.search);
    if (currentFilters.value.selectedFloorNo != null) {
      params.set('floors', currentFilters.value.selectedFloorNo.toString());
    } else {
      params.delete('floors');
    }
    if (currentFilters.value.selectedPriceRange?.min != null) {
      params.set('minPrice', currentFilters.value.selectedPriceRange?.min.toString());
    } else {
      params.delete('minPrice');
    }
    if (currentFilters.value.selectedPriceRange?.max != null) {
      params.set('maxPrice', currentFilters.value.selectedPriceRange?.max.toString());
    } else {
      params.delete('maxPrice');
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  } else {
    // Si la région a changé, rediriger vers la page appropriée
    if (regionHasChanged) {
      if (currentFilters.value.selectedRegion) {
        // Cas 1: Nouvelle region selectionnee -> page de choix de projets
        try {
          const regions = await getAllRegions();
          const selectedRegion = regions.find(r => r.id === currentFilters.value.selectedRegion);
          
          if (selectedRegion) {
            const url = getRegionWizardUrl(selectedRegion);
            window.location.href = url;
            return;
          }
        } catch (error) {
          console.error('❌ Failed to get region data for navigation:', error);
        }
      } else {
        // Cas 2: Région supprimée/null -> retour à la sélection de région
        try {
          const { rentalWizardFirstStepUrl } = useRentalLocalizedUrls();
          const { purchaseWizardFirstStepUrl } = usePurchaseLocalizedUrls();
          window.location.href = props.listingType === ListingTypeEnum.Rental ? rentalWizardFirstStepUrl : purchaseWizardFirstStepUrl;
          return;
        } catch (error) {
          console.error('❌ Failed to navigate to region selection:', error);
        }
      }
    }
  }
  
  emit('apply', { ...currentFilters.value });
  emit('close');
  
  window.scrollTo(0, 0);
};

// Charger les étages disponibles et la plage de prix depuis l'API
const loadAvailableFloors = async () => {
  try {
    let response;
    if (props.listingType === ListingTypeEnum.Rental) {
      response = await getUnitFilters(ListingTypeEnum.Rental, DEFAULT_RENTAL_VALID_UNIT_STATUSES);
    } else {
      response = await getUnitFilters(ListingTypeEnum.Sale, DEFAULT_PURCHASE_VALID_UNIT_STATUSES);
    }
    availableFloors.value = response.availableFloors || [];
    
    if (props.listingType === ListingTypeEnum.Rental) {
      priceRange.value = {
        min: response.minPrice || DEFAULT_MIN_PRICE,
        max: response.maxPrice || DEFAULT_MAX_PRICE
      };
    } else {
      priceRange.value = {
        min: response.minPrice || DEFAULT_PURCHASE_MIN_PRICE,
        max: response.maxPrice || DEFAULT_PURCHASE_MAX_PRICE
      };
    }
  } catch (error) {
    console.error('Failed to load rental unit filters:', error);
    availableFloors.value = [];
    priceRange.value = {
      min: props.listingType === ListingTypeEnum.Rental ? DEFAULT_MIN_PRICE : DEFAULT_PURCHASE_MIN_PRICE,
      max: props.listingType === ListingTypeEnum.Rental ? DEFAULT_MAX_PRICE : DEFAULT_PURCHASE_MAX_PRICE
    };
  }
};

onMounted(async () => {
  // Charger les étages disponibles au montage du composant
  await loadAvailableFloors();
  
  // Stocker la région initiale au montage pour détecter les changements ultérieurs
  const initialFilters = props.listingType === ListingTypeEnum.Rental ? loadFiltersFromRentalStorage() : loadFiltersFromPurchaseStorage();
  sessionOriginalRegion.value = initialFilters.regionId;
});

// Méthode exposée pour recharger les filtres depuis l'extérieur
const refreshFilters = () => {
  loadExistingFilters();
};

// Exposer la méthode pour qu'elle soit accessible via ref
defineExpose({
  refreshFilters
});

watch(() => props.show, (newValue) => {
  if (newValue) {
    // Charger les filtres existants quand le popup s'ouvre
    loadExistingFilters();
  }
});
</script>

<template>
  <div v-if="props.show" class="rental-units-filter-popup-container">
    <div class="rental-units-filter-popup">
      <div class="filter-popup-content">
        <div class="popup-header">
          <div class="popup-header-content">
            <div class="title">{{ t('forms.rentalFilters.filtersTitle') }}</div>
            <button @click="closePopup" class="close-button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/></svg>
            </button>
          </div>
        </div>
        <div class="popup-content">
          <div class="filtersPanel">
            <div class="filtersPanel-filters">
              <div class="container-mobile-only">
                <div class="filtersPanel-filter">
                  <RegionSearchField
                    v-model="currentFilters.selectedRegion"
                    class="filtersPanel-fieldset" />
                </div>
                <div class="filtersPanel-filter">
                  <SizeSearchField
                    v-model="currentFilters.selectedSize"
                    class="filtersPanel-fieldset" />
                </div>
                <div class="filtersPanel-filter">
                  <DateSearchField
                    v-model="currentFilters.selectedDate"
                    class="filtersPanel-fieldset" />
                </div>
                <div class="filtersPanel-filter">
                  <AnimalSearchField
                    v-if="props.listingType === ListingType.Rental"
                    v-model="rentalFilters.dogAllowed"
                    class="filtersPanel-fieldset" />
                </div>
              </div>
              <div class="filtersPanel-filter filter-only">
                <template v-if="floorOptions.length > 0">
                  <UnitsFloorSelectorFilter v-model="currentFilters.selectedFloorNo"
                    :options="floorOptions"
                    class="filtersPanel-fieldset" />
                </template>
              </div>
              <div class="filtersPanel-filter filter-only">
                <UnitsPriceRangeFilter v-model="currentFilters.selectedPriceRange"
                                       :min-value="priceRange.min"
                                       :max-value="priceRange.max"
                                       :step="100"
                                       :subtitle="t('forms.rentalFilters.pricePerMonth')"
                                       class="filtersPanel-fieldset" />
              </div>
            </div>
          </div>
          <!-- Boutons d'action -->
          <div class="filter-actions">
            <button @click="clearAllFilters" class="btn btn_secondary">
              {{ t('forms.rentalFilters.clearAll') }}
            </button>
            <button @click="applyFilters" class="btn search">
              {{ props.applyButtonText || t('forms.rentalFilters.viewProjects') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="bg" @click="closePopup"></div>
  </div>
</template>

<style lang="scss" scoped>
.rental-units-filter-popup-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 2.4rem;
}

.bg{
  position: absolute;
  top:0;
  left:0;
  width:100%;
  height:100%;
  background-color: rgba(121, 120, 120, 0.50);
  z-index: 0;
  transition: opacity 0.3s ease;
}

.rental-units-filter-popup {
    position: relative;
    z-index: 1;
    width: 628px;
    margin:0 auto;

  .filter-popup-content {
    background: #fff;
    border-radius: 2rem;
    padding: 2.4rem;
    margin: 0 auto;

    .popup-header {
      padding-bottom: 1.6rem;
      border-bottom:1px solid #a6b3a2;

      .popup-header-content{
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
      }

      .title{
        font-size: 2.4rem;
        font-weight: 700;
      }

      .close-button{
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 2.4rem;
        height: 2.4rem;
        padding: 0;
        cursor: pointer;
        
        &:hover{
          color: #475744;
        }

        svg{
          width:100%;
          height:100%;
        }
      }
    }

    .filtersPanel {      
      .filtersPanel-filters{
        .filtersPanel-filter{
          padding: 1.6rem 0;
          border-bottom:1px solid #a6b3a2;
        }

        .filtersPanel-fieldset{
          border:0;
          margin:0;
          padding: 0;

          legend{
            display: block;
            margin: 0 0 1.2rem;
            font-weight: 700;
            font-size: 1.8rem;
          }

          &:deep(legend){
            display: block;
            margin: 0 0 1.2rem;
            font-weight: 700;
            font-size: 1.8rem;
          }

          &:deep(p){
            margin:0 0 1.0rem 0;
          }

          &:deep(.numberRange){
            .slider-horizontal{
              width: calc(100% - 1.6rem);
              margin: 0 auto;
            }

            .numberRange-inputs{
              gap:2.4rem;
              flex-wrap: nowrap;

              .numberRange-inputContainer{
                display: block;
                width: 50%;
                flex: inherit;

                label {
                  display: block;
                  margin-bottom: 0.5rem;
                  font-weight: 500;
                  font-size: 1.4rem;
                  color: #333;
                }

                .input-numeric {
                  width: 100%;
                  padding: 0.8rem 1.2rem;
                  border: 1px solid #d1d5db;
                  border-radius: 0.8rem;
                  background-color: #f9fafb;
                  font-size: 1.4rem;
                  color: #374151;
                  
                  &:focus {
                    outline: none;
                    border-color: #475744;
                    background-color: #fff;
                    box-shadow: 0 0 0 2px rgba(71, 87, 68, 0.1);
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}

.filter-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap:2.4rem;
  margin-top:2.4rem;

  .btn{
    min-width: 25.0rem;
    width: 50%;

    &.btn_secondary{
      border:1px solid #A6B3A2;
    }

    &.search{
      color:#ffffff;
    }
  }
}

.container-mobile-only {
  display: none;
  
  @media screen and (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.6rem 2.4rem;
    flex-wrap: wrap;
    padding: 1.6rem 0;
    border-bottom: 1px solid #a6b3a2;

    .filtersPanel-filter{
      display: block;
      width: calc(50% - 1.2rem);
      border:0 !important;
      padding:0 !important;
    }
  }

  @media screen and (max-width: 784px) {
    display: block;

    .filtersPanel-filter{
      width: 100%;

      & + .filtersPanel-filter{
        margin-top: 1.6rem;
      }
    }
  }
}

@media screen and (max-width: 1024px){
  .rental-units-filter-popup{
    width:100%;

    .filter-popup-content{
      width: 100%;
    }
  }
  .region-purchase-browsing,
  .home-rental-browsing{
    .filter-only{
      display:none;
    }
  }
}

@media screen and (max-width: 767px){
  .rental-units-filter-popup{
    position: fixed;
    top: 6.6rem;
    max-width: 100%;
    z-index: 100;
    height: calc(100% - 6.6rem);
    min-width: 100%;
    max-height: 100%;

    .filter-popup-content{
      border-radius: 0;
      height: 100%;
      max-height: 100%;
      display: flex;
      min-width: auto;
      flex-direction: column;
      justify-content: flex-start;
      padding:2.4rem;

      .popup-header{
        .popup-header-content{
          justify-content: flex-start;
          .title{
            font-size: 1.8rem;
          }
        }
      }

      .popup-content{
        flex-grow: 2;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        overflow: auto;
      }

      .filtersPanel{
        flex-grow:2;

        .filtersPanel-filters{
          .filtersPanel-fieldset{
            &:deep(legend){
              font-size: 1.4rem;
              font-weight: 600;
              color: var(--text-color);
              margin-bottom: .8rem;
              display: block;
            }
            &:deep(p){
              font-size: 1.4rem;
              font-weight: 600;
              color: var(--text-color);
              margin-bottom: .8rem;
              display: block;
            }
            &:deep(.numberRange){
              .numberRange-inputs{

                .numberRange-inputContainer{
                  font-size: 1.4rem;
                  font-weight: 600;
                  color: var(--text-color);
                  margin-bottom: .8rem;
                  display: block;
                }
              }
            }
          }
        }
      }
    }
    .filter-actions{
      flex-direction: column;
      gap:1.6rem;

      .btn{
        width:100%;
      }
    }
  }
}

@media screen and (max-width: 767px) and (max-height: 700px){
  .rental-units-filter-popup{
    .filter-popup-content{
      .popup-content{
        padding-bottom:15.0rem;
      }

      .filter-actions{
        position: fixed;
        left: 0;
        bottom: 0;
        width: 100%;
        background-color: #ffffff;
        padding:2.0rem;
        box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.25);
        margin:0;
        z-index: 10;
      }
    }
  }
}
</style>
